import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner, Toggle } from "@aigntx/ui";
import { endpoints } from "../api";
import type { Blox, CatalogType, TenantSummary } from "../types";

function fmt(n: number): string {
  return n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
}

export function ControlPlanePage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<TenantSummary[] | null>(null);
  const [types, setTypes] = useState<CatalogType[]>([]);
  // Driven by the server, never a hand-kept list — the hardcoded one had already
  // drifted and was missing a BloX that exists, so it could not be switched on.
  const [blox, setBlox] = useState<Blox[]>([]);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState<
    { kind: "create" } | { kind: "duplicate" | "edit"; tenant: TenantSummary } | null
  >(null);

  useEffect(() => {
    Promise.all([endpoints.listTenants(), endpoints.getCatalog()])
      .then(([ts, cat]) => {
        setRows(ts);
        setTypes(cat.types);
        setBlox(cat.blox ?? []);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  function upsert(updated: TenantSummary) {
    setRows((rs) => {
      if (!rs) return [updated];
      const i = rs.findIndex((r) => r.slug === updated.slug);
      if (i >= 0) {
        const next = [...rs];
        next[i] = updated;
        return next;
      }
      return [...rs, updated].sort((a, b) => a.name.localeCompare(b.name));
    });
  }

  async function toggleModule(slug: string, mod: string, enabled: boolean) {
    upsert(await endpoints.setTenantModule(slug, mod, enabled));
  }
  async function toggleStatus(row: TenantSummary) {
    upsert(await endpoints.setTenantStatus(row.slug, row.status === "active" ? "suspended" : "active"));
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!rows)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  return (
    <div className="page">
      <header className="page__head between">
        <div>
          <h1 className="page__title">{t("admin.title")}</h1>
          <p className="page__sub">{t("admin.subtitle")}</p>
        </div>
        <Button onClick={() => setDialog({ kind: "create" })}>{t("admin.newTenant")}</Button>
      </header>

      <div className="tenants">
        {rows.length === 0 && <Card>{t("admin.noTenants")}</Card>}
        {rows.map((row) => {
          const pct =
            row.usage.tokensQuota > 0
              ? Math.min(100, Math.round((row.usage.tokensToday / row.usage.tokensQuota) * 100))
              : 0;
          return (
            <Card key={row.slug} className="tenant">
              <div className="tenant__head">
                <div>
                  <div className="tenant__name">{row.name}</div>
                  <div className="tenant__meta mono">
                    {row.slug} · {row.type}
                  </div>
                </div>
                <div className="tenant__status">
                  <Badge tone={row.status === "active" ? "good" : "danger"}>{t(`admin.${row.status}`)}</Badge>
                  <Button size="sm" variant="ghost" onClick={() => setDialog({ kind: "edit", tenant: row })}>
                    {t("common.edit")}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDialog({ kind: "duplicate", tenant: row })}>
                    {t("admin.duplicate")}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void toggleStatus(row)}>
                    {row.status === "active" ? t("admin.suspend") : t("admin.activate")}
                  </Button>
                </div>
              </div>

              <div className="tenant__usage">
                <div className="usage__label">
                  <span>{t("admin.usage")}</span>
                  <span className="mono">
                    {fmt(row.usage.tokensToday)} / {fmt(row.usage.tokensQuota)}
                  </span>
                </div>
                <div className="bar">
                  <span className={pct > 90 ? "bar__fill is-hot" : "bar__fill"} style={{ width: `${pct}%` }} />
                </div>
              </div>

              <div className="tenant__mods">
                <div className="mods__label">{t("blox.title")}</div>
                <div className="mods__grid">
                  {blox.map((b) => (
                    <Toggle
                      key={b.key}
                      id={`${row.slug}-${b.key}`}
                      label={
                        <span className="blox-row">
                          <span className="mono">{b.key}</span>
                          {/* say plainly whether switching it on gives anything */}
                          <Badge tone={b.status === "live" ? "good" : b.status === "building" ? "warn" : "neutral"}>
                            {t(`blox.${b.status}`)}
                          </Badge>
                          {b.status === "live" && <span className="blox-ver">v{b.version}</span>}
                        </span>
                      }
                      checked={row.modules.includes(b.key)}
                      onChange={(v) => void toggleModule(row.slug, b.key, v)}
                    />
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {dialog?.kind === "create" && (
        <CreateTenantModal types={types} onClose={() => setDialog(null)} onSaved={(t2) => { upsert(t2); setDialog(null); }} />
      )}
      {dialog?.kind === "duplicate" && (
        <DuplicateModal source={dialog.tenant} onClose={() => setDialog(null)} onSaved={(t2) => { upsert(t2); setDialog(null); }} />
      )}
      {dialog?.kind === "edit" && (
        <EditTenantModal tenant={dialog.tenant} types={types} onClose={() => setDialog(null)} onSaved={(t2) => { upsert(t2); setDialog(null); }} />
      )}
    </div>
  );
}

function ModalError({ msg }: { msg: string }) {
  return msg ? <div className="form-err">{msg}</div> : null;
}

function CreateTenantModal({
  types,
  onClose,
  onSaved,
}: {
  types: CatalogType[];
  onClose: () => void;
  onSaved: (t: TenantSummary) => void;
}) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [type, setType] = useState(types[0]?.id ?? "general");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onSaved(await endpoints.createTenant({ slug, name, type }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("admin.newTenant")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.create")}
          </Button>
        </>
      }
    >
      <ModalError msg={error} />
      <Field label={t("admin.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("admin.slug")} hint={t("admin.slugHint")}>
        <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="acme-eng" />
      </Field>
      <Field label={t("admin.type")}>
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          {types.map((ty) => (
            <option key={ty.id} value={ty.id}>
              {ty.label}
            </option>
          ))}
        </Select>
      </Field>
    </Modal>
  );
}

function DuplicateModal({
  source,
  onClose,
  onSaved,
}: {
  source: TenantSummary;
  onClose: () => void;
  onSaved: (t: TenantSummary) => void;
}) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState("");
  const [name, setName] = useState(`${source.name} (copy)`);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onSaved(await endpoints.duplicateTenant(source.slug, { slug, name }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`${t("admin.duplicate")} — ${source.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("admin.duplicate")}
          </Button>
        </>
      }
    >
      <ModalError msg={error} />
      <p className="page__sub">{t("admin.duplicateHint")}</p>
      <Field label={t("admin.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("admin.slug")} hint={t("admin.slugHint")}>
        <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
      </Field>
    </Modal>
  );
}

function EditTenantModal({
  tenant,
  types,
  onClose,
  onSaved,
}: {
  tenant: TenantSummary;
  types: CatalogType[];
  onClose: () => void;
  onSaved: (t: TenantSummary) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(tenant.name);
  const [type, setType] = useState(tenant.type);
  const [seats, setSeats] = useState(tenant.usage.seats);
  const [storage, setStorage] = useState(tenant.usage.storageGb);
  const [quota, setQuota] = useState(tenant.usage.tokensQuota);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onSaved(
        await endpoints.updateTenant(tenant.slug, {
          name,
          type,
          limits: { seats, storageGb: storage },
          ai: { quota: { tokensPerDay: quota } },
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={`${t("common.edit")} — ${tenant.name}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <ModalError msg={error} />
      <Field label={t("admin.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("admin.type")}>
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          {types.map((ty) => (
            <option key={ty.id} value={ty.id}>
              {ty.label}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid2-form">
        <Field label={t("dashboard.seats")}>
          <Input type="number" value={seats} onChange={(e) => setSeats(Number(e.target.value))} />
        </Field>
        <Field label={t("dashboard.storage")}>
          <Input type="number" value={storage} onChange={(e) => setStorage(Number(e.target.value))} />
        </Field>
      </div>
      <Field label={t("admin.quota")} hint={t("admin.quotaHint")}>
        <Input type="number" value={quota} onChange={(e) => setQuota(Number(e.target.value))} />
      </Field>
    </Modal>
  );
}
